/**
 * Non-React token store used by the API/WebSocket layer.
 *
 * The ZitadelProvider keeps this value in sync with the React state so that
 * plain fetch/WebSocket helpers can attach the current access token without
 * depending on React context.
 */
let currentToken: string | null = null;

export function setCurrentToken(token: string | null): void {
  currentToken = token;
}

export function getCurrentToken(): string | null {
  return currentToken;
}

/**
 * Decode the `exp` claim (epoch seconds) from a JWT, or null if the token
 * is missing/malformed. Used by the realtime transport to schedule a
 * near-expiry reconnect (INV-7).
 */
export function getJwtExp(token: string | null | undefined): number | null {
  if (!token) return null;
  const decoded = decodeJwtPayload(token);
  if (!decoded) return null;
  return typeof decoded.exp === 'number' ? decoded.exp : null;
}

/**
 * Decode the `sub` claim from a JWT, or null if the token is
 * missing/malformed. The MQTT transport uses this as the CONNECT
 * `username` so the go-auth http backend can route the auth check to
 * the right ACL cache entry (see `authz-sidecar.ts`).
 */
export function getJwtSub(token: string | null | undefined): string | null {
  if (!token) return null;
  const decoded = decodeJwtPayload(token);
  if (!decoded) return null;
  return typeof decoded.sub === 'string' ? decoded.sub : null;
}

function decodeJwtPayload(token: string): { sub?: unknown; exp?: unknown } | null {
  const parts = token.split('.');
  const payload = parts[1];
  if (!payload) return null;
  try {
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as { sub?: unknown; exp?: unknown };
  } catch {
    return null;
  }
}
